import { Award, Calendar, CircleDashed, Layers, MapPin } from 'lucide-react';
import { useMemo } from 'react';

import { type FilterChipDef, type FilterChipOption, type FilterToolbarSchema } from '@/components/composed';

import { REGION_OPTIONS } from './domain/types';

/** Group-by dimensions, also surfaced as the leading select chip in the toolbar. */
export const GROUP_BY_OPTIONS: FilterChipOption[] = [
  { value: 'region', label: 'Region' },
  { value: 'status', label: 'Status' },
  { value: 'tier', label: 'Tier' },
];

const STATUS_OPTIONS: FilterChipOption[] = [
  { value: 'Active', label: 'Active', color: '#10B981' },
  { value: 'Paused', label: 'Paused', color: '#F79009' },
  { value: 'Flagged', label: 'Flagged', color: '#F04438' },
  { value: 'Terminated', label: 'Terminated', color: '#6B7280' },
];

const REGION_FILTER_OPTIONS: FilterChipOption[] = REGION_OPTIONS.map((region) => ({
  value: region,
  label: region,
}));

export type BreakdownGroupByDimension = 'region' | 'status' | 'tier';

export interface UseAffiliateBreakdownFiltersSchemaArgs {
  tierOptions: FilterChipOption[];
  /** The current X-axis dimension. The matching chip is omitted to avoid
   * filtering on the same dimension you're grouping by. */
  groupBy: BreakdownGroupByDimension;
}

export const useAffiliateBreakdownFiltersSchema = ({
  tierOptions,
  groupBy,
}: UseAffiliateBreakdownFiltersSchemaArgs): FilterToolbarSchema =>
  useMemo<FilterToolbarSchema>(() => {
    // Group-by selector lives in the toolbar as the leading chip. It always has
    // a value (default 'region') and is never omitted — unlike the filter chips
    // below, where the chip matching the current group dimension is hidden so
    // you can't filter on the same dimension you're grouping by.
    const chips: FilterChipDef[] = [
      {
        name: 'groupBy',
        type: 'select',
        label: 'Group by',
        icon: Layers,
        defaultValue: 'region',
        options: GROUP_BY_OPTIONS,
      },
    ];

    if (groupBy !== 'status') {
      chips.push({
        name: 'statuses',
        type: 'combobox',
        multi: true,
        label: 'Status',
        icon: CircleDashed,
        options: STATUS_OPTIONS,
        selectAllLabel: 'Select all statuses',
      });
    }

    if (groupBy !== 'region') {
      chips.push({
        name: 'regions',
        type: 'combobox',
        multi: true,
        label: 'Region',
        icon: MapPin,
        options: REGION_FILTER_OPTIONS,
        selectAllLabel: 'Select all regions',
      });
    }

    if (groupBy !== 'tier') {
      chips.push({
        name: 'tiers',
        type: 'combobox',
        multi: true,
        label: 'Tier',
        icon: Award,
        options: tierOptions,
        selectAllLabel: 'Select all tiers',
      });
    }

    return {
      chips,
      date: {
        name: 'date',
        type: 'date-range',
        icon: Calendar,
        label: 'Date range',
        fromKey: 'dateFrom',
        toKey: 'dateTo',
      },
    };
  }, [tierOptions, groupBy]);
